import React, { useEffect, useMemo, useState } from "react";
import Barcode from "react-barcode";
import { Alert, Button, FormGroup, Input, Label, Table } from "reactstrap";

import CatalogSidePanel from "../Catalog/CatalogSidePanel";
import { useTranslation } from "../../hooks/useTranslation";
import { buildBarcodeLabelJob, printBarcodeLabelJob } from "./barcodeLabelJob";
import {
  PRINT_BLOCK_REASONS,
  getPrintItemKey,
  partitionPrintItems,
} from "./catalogBarcodePrintItems";
import {
  buildCopiesByQuantityMode,
  getOnHandForPrintItem,
  normalizeCopiesInputValue,
  PRINT_QUANTITY_MODES,
} from "./catalogBarcodePrintQuantity";
import { getBarcodeLabelData as getCatalogBarcodeLabelData } from "./engine/barcodeLabelTemplate";
import {
  DEFAULT_DEVICE_CALIBRATION,
  DEFAULT_SHEET_GEOMETRY,
  normalizeDeviceCalibration,
  normalizeSheetGeometry,
} from "./engine/sheetLayout";

// This panel keeps the calibration it has always used: sheet geometry and this
// device's printer offsets stored together in the browser.
const DEFAULT_PRINT_CALIBRATION = { ...DEFAULT_SHEET_GEOMETRY, ...DEFAULT_DEVICE_CALIBRATION };
const normalizePrintCalibration = (calibration) => ({
  ...normalizeSheetGeometry(calibration),
  ...normalizeDeviceCalibration(calibration),
});

const BLOCK_REASON_KEYS = {
  [PRINT_BLOCK_REASONS.MISSING_BARCODE]: "blockedMissingBarcode",
  [PRINT_BLOCK_REASONS.SERIALIZED_NEEDS_UNIT]: "blockedSerializedNeedsUnit",
  [PRINT_BLOCK_REASONS.UNRENDERABLE_BARCODE]: "blockedUnrenderable",
};

const PRINT_CALIBRATION_STORAGE_KEY = "bw-barcode-print-calibration-v1";
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const SHOW_INLINE_LABEL_PREVIEW = false;
const LAYOUT_PRESETS = [
  {
    id: "maximum-density",
    labelKey: "presetDensity",
    descriptionKey: "presetDensityHelp",
    values: {
      safePadXMM: 2,
      safePadYMM: 2,
      labelColumnGapMM: 1.4,
      labelRowGapMM: 1,
    },
  },
  {
    id: "compact",
    labelKey: "presetCompact",
    descriptionKey: "presetCompactHelp",
    values: {
      safePadXMM: 2.5,
      safePadYMM: 2.5,
      labelColumnGapMM: 1.7,
      labelRowGapMM: 1.2,
    },
  },
  {
    id: "balanced",
    labelKey: "presetBalanced",
    descriptionKey: "presetBalancedHelp",
    values: {
      safePadXMM: 3,
      safePadYMM: 3,
      labelColumnGapMM: 2,
      labelRowGapMM: 1.5,
    },
  },
  {
    id: "spacious",
    labelKey: "presetSpacious",
    descriptionKey: "presetSpaciousHelp",
    values: {
      safePadXMM: 4,
      safePadYMM: 4,
      labelColumnGapMM: 2.4,
      labelRowGapMM: 1.9,
    },
  },
];

const QUANTITY_MODE_OPTIONS = [
  {
    id: PRINT_QUANTITY_MODES.ONE_PER_VARIANT,
    labelKey: "modeOnePerVariant",
    helperKey: "modeOnePerVariantHelp",
  },
  {
    id: PRINT_QUANTITY_MODES.MATCH_ON_HAND,
    labelKey: "modeMatchStock",
    helperKey: "modeMatchStockHelp",
  },
];

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch (_error) {
    return value;
  }
};

// Display copy lives in labelPrintTranslations.js ("labelPrint" namespace).
const fillCopy = (text, values = {}) =>
  Object.entries(values).reduce((result, [key, value]) => result.split(`{${key}}`).join(String(value)), text);

const formatCalibrationValue = (value, unit, decimals = 1) =>
  `${Number(value).toFixed(decimals)}${unit ? ` ${unit}` : ""}`;

const calculateSheetMetrics = (calibration) => {
  const printableWidthMM = A4_WIDTH_MM - calibration.safePadXMM * 2;
  const printableHeightMM = A4_HEIGHT_MM - calibration.safePadYMM * 2;
  const labelWidthMM =
    (printableWidthMM - (calibration.columns - 1) * calibration.labelColumnGapMM) /
    calibration.columns;
  const labelHeightMM =
    (printableHeightMM - (calibration.rows - 1) * calibration.labelRowGapMM) /
    calibration.rows;

  return {
    printableWidthMM,
    printableHeightMM,
    labelWidthMM,
    labelHeightMM,
    labelsPerPage: calibration.columns * calibration.rows,
  };
};

function CatalogBarcodeLabelPreview({ item }) {
  const label = getCatalogBarcodeLabelData(item);

  return (
    <article className="catalog-label-card-preview">
      <div className="catalog-label-card-preview__copy">
        <div className="catalog-label-card-preview__product">
          {label.productName || label.modelCode || "Catalog item"}
        </div>
      </div>

      <div className="catalog-label-card-preview__size-row">
        <div className="catalog-label-card-preview__color-row">
          <span
            className="catalog-label-card-preview__color-dot"
            style={{ background: label.colorTone }}
            aria-hidden="true"
          />
          <strong className="catalog-label-card-preview__color-copy">{label.color}</strong>
        </div>
        <div className="catalog-label-card-preview__size">{label.size}</div>
      </div>

      <div className="catalog-label-card-preview__barcode-shell">
        <div className="catalog-label-card-preview__barcode">
          <Barcode
            value={label.barcode}
            format="CODE128"
            displayValue={false}
            width={1}
            height={24}
            margin={0}
          />
        </div>
        <div className="catalog-label-card-preview__barcode-text">{label.barcode}</div>
      </div>

      <div className="catalog-label-card-preview__price">
        {label.price || "\u00A0"}
      </div>
    </article>
  );
}

function CatalogPrintControl({
  lp,
  label,
  helper,
  value,
  unit,
  step,
  onAdjust,
  title,
  decimals = 1,
}) {
  return (
    <div className="catalog-print-calibration-control">
      <div className="catalog-print-calibration-control__meta">
        <span className="catalog-print-calibration-control__label" title={title}>
          {label}
        </span>
        <span className="catalog-print-calibration-control__helper">{helper}</span>
      </div>
      <div className="catalog-print-calibration-control__stepper">
        <Button
          type="button"
          color="secondary"
          outline
          size="sm"
          className="catalog-print-calibration-control__button"
          onClick={() => onAdjust(-step)}
          title={fillCopy(lp("decrease"), { label })}
        >
          −
        </Button>
        <div className="catalog-print-calibration-control__value">
          <strong>{formatCalibrationValue(value, unit, decimals)}</strong>
        </div>
        <Button
          type="button"
          color="secondary"
          outline
          size="sm"
          className="catalog-print-calibration-control__button"
          onClick={() => onAdjust(step)}
          title={fillCopy(lp("increase"), { label })}
        >
          +
        </Button>
      </div>
    </div>
  );
}

function CatalogPrintCalibrationPanel({
  lp,
  calibration,
  metrics,
  onAdjust,
  onApplyPreset,
  onResetAlignment,
  onRestoreDefaults,
}) {
  return (
    <section className="catalog-print-calibration">
      <div className="catalog-print-calibration__header">
        <div>
          <h4>{lp("calibrationTitle")}</h4>
          <p>{lp("calibrationCopy")}</p>
        </div>
        <div className="catalog-print-calibration__summary">
          <strong>
            {fillCopy(lp("calibrationSummary"), {
              columns: calibration.columns,
              rows: calibration.rows,
              count: metrics.labelsPerPage,
            })}
          </strong>
          <span>
            {fillCopy(lp("labelSize"), {
              width: formatCalibrationValue(metrics.labelWidthMM, "mm"),
              height: formatCalibrationValue(metrics.labelHeightMM, "mm"),
            })}
          </span>
        </div>
      </div>

      <div className="catalog-print-calibration__presets">
        {LAYOUT_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className="catalog-print-calibration__preset"
            onClick={() => onApplyPreset(preset.values)}
            title={lp(preset.descriptionKey)}
          >
            <strong>{lp(preset.labelKey)}</strong>
            <span>{lp(preset.descriptionKey)}</span>
          </button>
        ))}
      </div>

      <div className="catalog-print-calibration__sections">
        <section className="catalog-print-calibration-section">
          <div className="catalog-print-calibration-section__header">
            <h5>{lp("sectionLayout")}</h5>
            <p>{lp("sectionLayoutCopy")}</p>
          </div>
          <div className="catalog-print-calibration-section__grid catalog-print-calibration-section__grid--two">
            <CatalogPrintControl
              lp={lp}
              label={lp("columns")}
              helper={lp("columnsHelp")}
              value={calibration.columns}
              unit=""
              step={1}
              decimals={0}
              onAdjust={(delta) => onAdjust("columns", delta)}
              title={lp("columnsTitle")}
            />
            <CatalogPrintControl
              lp={lp}
              label={lp("rows")}
              helper={lp("rowsHelp")}
              value={calibration.rows}
              unit=""
              step={1}
              decimals={0}
              onAdjust={(delta) => onAdjust("rows", delta)}
              title={lp("rowsTitle")}
            />
          </div>
        </section>

        <section className="catalog-print-calibration-section">
          <div className="catalog-print-calibration-section__header">
            <h5>{lp("sectionSpacing")}</h5>
            <p>{lp("sectionSpacingCopy")}</p>
          </div>
          <div className="catalog-print-calibration-section__grid">
            <CatalogPrintControl
              lp={lp}
              label={lp("gapX")}
              helper={lp("gapXHelp")}
              value={calibration.labelColumnGapMM}
              unit="mm"
              step={0.5}
              onAdjust={(delta) => onAdjust("labelColumnGapMM", delta)}
              title={lp("gapXTitle")}
            />
            <CatalogPrintControl
              lp={lp}
              label={lp("gapY")}
              helper={lp("gapYHelp")}
              value={calibration.labelRowGapMM}
              unit="mm"
              step={0.5}
              onAdjust={(delta) => onAdjust("labelRowGapMM", delta)}
              title={lp("gapYTitle")}
            />
            <CatalogPrintControl
              lp={lp}
              label={lp("padX")}
              helper={lp("padXHelp")}
              value={calibration.safePadXMM}
              unit="mm"
              step={0.5}
              onAdjust={(delta) => onAdjust("safePadXMM", delta)}
              title={lp("padXTitle")}
            />
            <CatalogPrintControl
              lp={lp}
              label={lp("padY")}
              helper={lp("padYHelp")}
              value={calibration.safePadYMM}
              unit="mm"
              step={0.5}
              onAdjust={(delta) => onAdjust("safePadYMM", delta)}
              title={lp("padYTitle")}
            />
          </div>
        </section>

        <section className="catalog-print-calibration-section">
          <div className="catalog-print-calibration-section__header">
            <h5>{lp("sectionAlignment")}</h5>
            <p>{lp("sectionAlignmentCopy")}</p>
          </div>
          <div className="catalog-print-calibration-section__grid catalog-print-calibration-section__grid--two">
            <CatalogPrintControl
              lp={lp}
              label={lp("offsetX")}
              helper={lp("offsetXHelp")}
              value={calibration.printOffsetXMM}
              unit="mm"
              step={0.5}
              onAdjust={(delta) => onAdjust("printOffsetXMM", delta)}
              title={lp("offsetXTitle")}
            />
            <CatalogPrintControl
              lp={lp}
              label={lp("offsetY")}
              helper={lp("offsetYHelp")}
              value={calibration.printOffsetYMM}
              unit="mm"
              step={0.5}
              onAdjust={(delta) => onAdjust("printOffsetYMM", delta)}
              title={lp("offsetYTitle")}
            />
          </div>
        </section>

        <section className="catalog-print-calibration-section catalog-print-calibration-section--actions">
          <div className="catalog-print-calibration-section__header">
            <h5>{lp("sectionActions")}</h5>
            <p>{lp("sectionActionsCopy")}</p>
          </div>
          <div className="catalog-print-calibration__actions">
            <Button
              type="button"
              color="secondary"
              outline
              className="catalog-print-calibration__action-button"
              onClick={onResetAlignment}
            >
              {lp("resetAlignment")}
            </Button>
            <Button
              type="button"
              color="link"
              className="catalog-print-calibration__action-link"
              onClick={onRestoreDefaults}
            >
              {lp("restoreDefaults")}
            </Button>
          </div>
        </section>
      </div>
    </section>
  );
}

function CatalogPrintVariantRow({ item, lp }) {
  const label = getCatalogBarcodeLabelData(item);
  const availableStock = getOnHandForPrintItem(item);
  const tokens = [
    label.productName || item.productTitle,
    label.color,
    label.size,
  ].filter(Boolean);

  return (
    <div
      className="catalog-print-variant-line"
      title={`${tokens.join(" · ")} · ${fillCopy(lp("stock"), { count: availableStock })}`}
    >
      <div className="catalog-print-variant-line__primary">
        {tokens.map((token, index) => (
          <React.Fragment key={`${token}-${index}`}>
            {index ? <span className="catalog-print-variant-line__divider">·</span> : null}
            <span className="catalog-print-variant-line__token">{token}</span>
          </React.Fragment>
        ))}
      </div>
      <span className="catalog-print-variant-line__stock">
        {fillCopy(lp("stock"), { count: availableStock })}
      </span>
    </div>
  );
}

function CatalogPrintQuantityModePanel({
  lp,
  quantityMode,
  onChangeMode,
  onRecalculateCopies,
  totalLabels,
  totalPages,
  labelsPerPage,
  calibration,
}) {
  return (
    <section className="catalog-print-quantity-mode">
      <div className="catalog-print-quantity-mode__header">
        <div>
          <h4>{lp("quantityTitle")}</h4>
          <p>{lp("quantityCopy")}</p>
        </div>
        <div className="catalog-print-quantity-mode__summary">
          <strong>
            {fillCopy(lp("labelsPerPageSummary"), {
              columns: calibration.columns,
              rows: calibration.rows,
              count: labelsPerPage,
            })}
          </strong>
          <span>{fillCopy(lp("totalLabelsSummary"), { count: totalLabels })}</span>
          <span>{fillCopy(lp("pagesSummary"), { count: totalPages })}</span>
        </div>
      </div>

      <div className="catalog-print-quantity-mode__options">
        {QUANTITY_MODE_OPTIONS.map((option) => (
          <label
            key={option.id}
            className={`catalog-print-quantity-mode__option${
              quantityMode === option.id ? " is-active" : ""
            }`}
          >
            <Input
              type="radio"
              name="catalog-print-quantity-mode"
              checked={quantityMode === option.id}
              onChange={() => onChangeMode(option.id)}
            />
            <div>
              <strong>{lp(option.labelKey)}</strong>
              <span>{lp(option.helperKey)}</span>
            </div>
          </label>
        ))}
      </div>

      <div className="catalog-print-quantity-mode__actions">
        <Button
          type="button"
          color="secondary"
          outline
          size="sm"
          onClick={onRecalculateCopies}
        >
          {lp("recalculate")}
        </Button>
      </div>
    </section>
  );
}

function CatalogBarcodeSheetPreview({ lp, items, totalLabels, calibration }) {
  const labelsPerPage = calibration.columns * calibration.rows;
  const visibleItems = items.slice(0, labelsPerPage);
  const fillerCount = Math.max(0, labelsPerPage - visibleItems.length);
  const safePadXPercent = (calibration.safePadXMM / 210) * 100;
  const safePadYPercent = (calibration.safePadYMM / 297) * 100;
  const columnGapPercent = (calibration.labelColumnGapMM / 210) * 100;
  const rowGapPercent = (calibration.labelRowGapMM / 297) * 100;
  const offsetXPercent = (calibration.printOffsetXMM / 210) * 100;
  const offsetYPercent = (calibration.printOffsetYMM / 297) * 100;

  return (
    <section className="catalog-label-sheet-preview-section">
      <div className="catalog-label-sheet-preview-section__header">
        <div>
          <h4>{lp("sheetTitle")}</h4>
          <p>{fillCopy(lp("sheetCopy"), { columns: calibration.columns, rows: calibration.rows })}</p>
        </div>
        {totalLabels > labelsPerPage ? (
          <span className="catalog-label-sheet-preview-section__note">
            {fillCopy(lp(totalLabels - labelsPerPage === 1 ? "sheetMoreOne" : "sheetMoreMany"), {
              count: totalLabels - labelsPerPage,
            })}
          </span>
        ) : null}
      </div>

      <div className="catalog-label-sheet-preview">
        <div
          className="catalog-label-sheet-preview__page"
          style={{
            "--preview-safe-pad-x": `${safePadXPercent}%`,
            "--preview-safe-pad-y": `${safePadYPercent}%`,
            "--preview-column-gap": `${columnGapPercent}%`,
            "--preview-row-gap": `${rowGapPercent}%`,
            "--preview-offset-x": `${offsetXPercent}%`,
            "--preview-offset-y": `${offsetYPercent}%`,
            gridTemplateColumns: `repeat(${calibration.columns}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${calibration.rows}, minmax(0, 1fr))`,
          }}
        >
          {visibleItems.map((item) => (
            <CatalogBarcodeLabelPreview
              key={`${getPrintItemKey(item)}-${item.__copyIndex || 0}`}
              item={item}
            />
          ))}
          {Array.from({ length: fillerCount }, (_entry, index) => (
            <div
              key={`empty-label-slot-${index + 1}`}
              className="catalog-label-sheet-preview__empty"
              aria-hidden="true"
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/** Items that cannot be printed, each named with the reason. Never hidden. */
function CatalogPrintIssueList({ tone, title, entries }) {
  if (!entries.length) return null;

  return (
    <Alert color={tone} className="catalog-print-warning catalog-print-issues">
      <strong>{title}</strong>
      <ul className="catalog-print-issues__list">
        {entries.map((entry) => (
          <li key={entry.key}>
            <strong>{entry.name}</strong> — {entry.reason}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

const describePrintItem = (item) => {
  const label = getCatalogBarcodeLabelData(item);
  return [label.productName || item.productTitle, label.color, label.size]
    .filter((token) => token && token !== "\u2014")
    .join(" · ");
};

// Older callers key initial copies by variant id; unit labels by print key.
const resolveInitialCopies = (initialCopiesById, item) => {
  const byKey = initialCopiesById?.[getPrintItemKey(item)];
  if (byKey != null) return byKey;
  return item.kind === "unit" ? undefined : initialCopiesById?.[item.variantId];
};

function CatalogBarcodePrintModal({
  isOpen,
  toggle,
  items,
  onPrinted,
  selectable = false,
  isLoading = false,
  initialCopiesById = null,
  title,
  description,
  warningMessage = "",
  emptyMessage,
  loadingMessage,
}) {
  const t = useTranslation();
  const lp = (key) => t(`labelPrint.${key}`);
  const [copiesById, setCopiesById] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [quantityMode, setQuantityMode] = useState(
    PRINT_QUANTITY_MODES.ONE_PER_VARIANT
  );
  const [manualCopiesById, setManualCopiesById] = useState({});
  const [calibration, setCalibration] = useState(DEFAULT_PRINT_CALIBRATION);
  const [hasLoadedCalibration, setHasLoadedCalibration] = useState(false);

  // Unprintable items (no barcode, serialized without a unit label) are listed
  // with a reason instead of being dropped silently.
  const { printable: printableItems, blocked: blockedItems } = useMemo(
    () => partitionPrintItems((Array.isArray(items) ? items : []).filter(Boolean)),
    [items]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const nextCopiesById =
      initialCopiesById && Object.keys(initialCopiesById).length
        ? printableItems.reduce((accumulator, item) => {
            accumulator[getPrintItemKey(item)] = normalizeCopiesInputValue(
              resolveInitialCopies(initialCopiesById, item),
              1
            );
            return accumulator;
          }, {})
        : buildCopiesByQuantityMode(
            printableItems,
            PRINT_QUANTITY_MODES.ONE_PER_VARIANT
          );

    setCopiesById(nextCopiesById);
    setManualCopiesById(
      printableItems.reduce((accumulator, item) => {
        accumulator[getPrintItemKey(item)] = false;
        return accumulator;
      }, {})
    );
    setSelectedIds(printableItems.map(getPrintItemKey));
    setQuantityMode(PRINT_QUANTITY_MODES.ONE_PER_VARIANT);
  }, [initialCopiesById, isOpen, printableItems]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const stored = window.localStorage.getItem(PRINT_CALIBRATION_STORAGE_KEY);
      if (!stored) {
        setHasLoadedCalibration(true);
        return;
      }

      setCalibration(normalizePrintCalibration(JSON.parse(stored)));
    } catch (_error) {}
    setHasLoadedCalibration(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !hasLoadedCalibration) {
      return;
    }

    window.localStorage.setItem(
      PRINT_CALIBRATION_STORAGE_KEY,
      JSON.stringify(calibration)
    );
  }, [calibration, hasLoadedCalibration]);

  const selectedPrintableItems = useMemo(() => {
    if (!selectable) {
      return printableItems;
    }

    const selectedSet = new Set(selectedIds);
    return printableItems.filter((item) => selectedSet.has(getPrintItemKey(item)));
  }, [printableItems, selectable, selectedIds]);

  // Barcodes are rendered locally and validated before anything can print.
  const job = useMemo(
    () =>
      isOpen
        ? buildBarcodeLabelJob({
            items: selectedPrintableItems,
            copiesByKey: copiesById,
            geometry: calibration,
          })
        : { lines: [], labels: [], totalLabels: 0, failures: [], densityWarnings: [] },
    [calibration, copiesById, isOpen, selectedPrintableItems]
  );
  const totalLabels = job.totalLabels;

  const sheetMetrics = useMemo(
    () => calculateSheetMetrics(calibration),
    [calibration]
  );

  const totalPages = useMemo(() => {
    if (!sheetMetrics.labelsPerPage || totalLabels <= 0) {
      return 0;
    }

    return Math.ceil(totalLabels / sheetMetrics.labelsPerPage);
  }, [sheetMetrics.labelsPerPage, totalLabels]);

  const previewSheetItems = useMemo(
    () =>
      job.lines.flatMap((line) =>
        Array.from({ length: line.copies }, (_entry, index) => ({
          ...line.item,
          __copyIndex: index + 1,
        }))
      ),
    [job.lines]
  );

  const handleCopiesChange = (printKey, value) => {
    setManualCopiesById((current) => ({
      ...current,
      [printKey]: true,
    }));
    setCopiesById((current) => ({
      ...current,
      [printKey]: normalizeCopiesInputValue(value, 0),
    }));
  };

  const handleToggleSelection = (printKey) => {
    setSelectedIds((current) =>
      current.includes(printKey)
        ? current.filter((id) => id !== printKey)
        : [...current, printKey]
    );
  };

  const handleSelectAll = () => {
    setSelectedIds(printableItems.map(getPrintItemKey));
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  const applyQuantityMode = (mode, { force = false } = {}) => {
    const computedCopies = buildCopiesByQuantityMode(printableItems, mode);

    setCopiesById((current) =>
      printableItems.reduce((accumulator, item) => {
        const printKey = getPrintItemKey(item);
        const shouldPreserve = !force && manualCopiesById[printKey];
        accumulator[printKey] = shouldPreserve
          ? normalizeCopiesInputValue(current[printKey], 0)
          : normalizeCopiesInputValue(computedCopies[printKey], 0);
        return accumulator;
      }, {})
    );

    if (force) {
      setManualCopiesById(
        printableItems.reduce((accumulator, item) => {
          accumulator[getPrintItemKey(item)] = false;
          return accumulator;
        }, {})
      );
    }

    if (selectable && mode === PRINT_QUANTITY_MODES.MATCH_ON_HAND) {
      setSelectedIds((current) => {
        const currentSet = new Set(current);

        printableItems.forEach((item) => {
          const key = getPrintItemKey(item);
          if (!force && manualCopiesById[key]) {
            return;
          }

          if (normalizeCopiesInputValue(computedCopies[key], 0) <= 0) {
            currentSet.delete(key);
          }
        });

        return printableItems
          .map(getPrintItemKey)
          .filter((key) => currentSet.has(key));
      });
    }
  };

  const handleQuantityModeChange = (mode) => {
    setQuantityMode(mode);
    applyQuantityMode(mode);
  };

  const handleRecalculateCopies = () => {
    applyQuantityMode(quantityMode, { force: true });
  };

  const handlePrint = () => {
    // Never prints a partial job: blocked by any barcode that cannot render.
    printBarcodeLabelJob({ job, geometry: calibration, calibration, startPosition: 1 });

    onPrinted?.();
    toggle?.();
  };

  const handleCalibrationAdjust = (key, delta) => {
    setCalibration((current) =>
      normalizePrintCalibration({
        ...current,
        [key]: Number(current[key] || 0) + delta,
      })
    );
  };

  const handleApplyPreset = (values) => {
    setCalibration((current) =>
      normalizePrintCalibration({
        ...current,
        ...values,
      })
    );
  };

  const handleResetAlignment = () => {
    setCalibration((current) =>
      normalizePrintCalibration({
        ...current,
        printOffsetXMM: DEFAULT_PRINT_CALIBRATION.printOffsetXMM,
        printOffsetYMM: DEFAULT_PRINT_CALIBRATION.printOffsetYMM,
      })
    );
  };

  const handleRestoreDefaults = () => {
    setCalibration({ ...DEFAULT_PRINT_CALIBRATION });
  };

  return (
    <CatalogSidePanel
      isOpen={isOpen}
      onClose={toggle}
      title={title || lp("title")}
      description={description || lp("description")}
      closeLabel={lp("close")}
      footer={
        <>
          <Button color="secondary" outline onClick={toggle}>
            {lp("cancel")}
          </Button>
          <Button
            color="primary"
            onClick={handlePrint}
            disabled={
              !selectedPrintableItems.length ||
              totalLabels <= 0 ||
              isLoading ||
              job.failures.length > 0
            }
          >
            {lp("print")}
          </Button>
        </>
      }
    >
        <div className="catalog-print-summary">
          <div>
            <span>{lp("variantsSelected")}</span>
            <strong>{selectedPrintableItems.length}</strong>
          </div>
          <div>
            <span>{lp("totalLabels")}</span>
            <strong>{totalLabels}</strong>
          </div>
          <div>
            <span>{lp("pages")}</span>
            <strong>{totalPages}</strong>
          </div>
        </div>

        {warningMessage ? (
          <Alert color="warning" className="catalog-print-warning">
            {warningMessage}
          </Alert>
        ) : null}

        <CatalogPrintIssueList
          tone="danger"
          title={lp("failuresTitle")}
          entries={job.failures.map((failure) => ({
            key: failure.key,
            name: describePrintItem(failure.item),
            reason: lp(BLOCK_REASON_KEYS[failure.reason]).replace(
              "{barcode}",
              failure.item.barcode || "—"
            ),
          }))}
        />
        <CatalogPrintIssueList
          tone="warning"
          title={lp("blockedTitle").replace("{count}", String(blockedItems.length))}
          entries={blockedItems.map((item) => ({
            key: getPrintItemKey(item),
            name: describePrintItem(item),
            reason: lp(BLOCK_REASON_KEYS[item.blockedReason] || "blockedMissingBarcode"),
          }))}
        />
        <CatalogPrintIssueList
          tone="warning"
          title={lp("densityTitle")}
          entries={job.densityWarnings.map((warning) => ({
            key: warning.key,
            name: describePrintItem(warning.item),
            reason: lp("densityReason")
              .replace("{barcode}", warning.item.barcode)
              .replace("{width}", warning.moduleWidthMM.toFixed(2)),
          }))}
        />

        {selectable ? (
          <div className="catalog-print-selection-bar">
            <Button
              type="button"
              color="link"
              size="sm"
              className="catalog-print-selection-button"
              onClick={handleSelectAll}
              disabled={!printableItems.length}
            >
              {lp("selectAll")}
            </Button>
            <Button
              type="button"
              color="link"
              size="sm"
              className="catalog-print-selection-button"
              onClick={handleClearSelection}
              disabled={!selectedIds.length}
            >
              {lp("clear")}
            </Button>
          </div>
        ) : null}

        <CatalogPrintQuantityModePanel
          lp={lp}
          quantityMode={quantityMode}
          onChangeMode={handleQuantityModeChange}
          onRecalculateCopies={handleRecalculateCopies}
          totalLabels={totalLabels}
          totalPages={totalPages}
          labelsPerPage={sheetMetrics.labelsPerPage}
          calibration={calibration}
        />

        <CatalogPrintCalibrationPanel
          lp={lp}
          calibration={calibration}
          metrics={sheetMetrics}
          onAdjust={handleCalibrationAdjust}
          onApplyPreset={handleApplyPreset}
          onResetAlignment={handleResetAlignment}
          onRestoreDefaults={handleRestoreDefaults}
        />

        <div className="catalog-studio-preview-table">
          {isLoading ? (
            <div className="catalog-print-loading">{loadingMessage || lp("loading")}</div>
          ) : !printableItems.length ? (
            <div className="catalog-print-loading">{emptyMessage || lp("empty")}</div>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  {selectable ? <th className="catalog-print-select-cell">{lp("colPick")}</th> : null}
                  <th>{lp("colVariant")}</th>
                  <th>{lp("colBarcode")}</th>
                  {selectable ? <th>{lp("colAdded")}</th> : null}
                  <th>{lp("colCopies")}</th>
                  {SHOW_INLINE_LABEL_PREVIEW ? <th>{lp("colLabel")}</th> : null}
                </tr>
              </thead>
              <tbody>
                {printableItems.map((item) => {
                  const printKey = getPrintItemKey(item);
                  const inputId = `copies-${printKey.replace(/[^a-z0-9]+/gi, "-")}`;
                  const isSelected = !selectable || selectedIds.includes(printKey);

                  return (
                    <tr key={printKey} className={isSelected ? "" : "catalog-print-row-muted"}>
                      {selectable ? (
                        <td className="catalog-print-select-cell">
                          <Input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelection(printKey)}
                          />
                        </td>
                      ) : null}
                      <td>
                        <CatalogPrintVariantRow item={item} lp={lp} />
                      </td>
                      <td className="catalog-print-barcode-cell">
                        <code>{item.barcode}</code>
                      </td>
                      {selectable ? (
                        <td>
                          <span className="catalog-ops-muted">
                            {formatDateTime(item.createdAt)}
                          </span>
                        </td>
                      ) : null}
                      <td className="catalog-print-copies-cell">
                        <FormGroup className="mb-0">
                          <Label className="sr-only" for={inputId}>
                            {lp("colCopies")}
                          </Label>
                          <Input
                            id={inputId}
                            type="number"
                            min="0"
                            step="1"
                            value={copiesById[printKey] ?? 0}
                            onChange={(event) =>
                              handleCopiesChange(printKey, event.target.value)
                            }
                            disabled={!isSelected}
                          />
                        </FormGroup>
                      </td>
                      {SHOW_INLINE_LABEL_PREVIEW ? (
                        <td>
                          <div className="catalog-print-preview">
                            <CatalogBarcodeLabelPreview item={item} />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>

        {selectedPrintableItems.length && totalLabels > 0 ? (
          <CatalogBarcodeSheetPreview
            lp={lp}
            items={previewSheetItems}
            totalLabels={totalLabels}
            calibration={calibration}
          />
        ) : null}
    </CatalogSidePanel>
  );
}

export default CatalogBarcodePrintModal;
